/**
 * SIP transport reconnection with node_state gating and the REGISTER revocation rule.
 *
 * - backoff 1, 2, 4, 8 s then 15 s cap, ±30 % jitter, unlimited attempts; `ua.reconnect()`
 *   then always `register()`
 * - while the relay reports node_state restarting|down the attempts are HELD; `up` releases
 *   them after one jittered delay so a thousand pop-outs do not hit the node at once
 * - a 401/403 on a REGISTER refresh is revocation only when the relay is healthy and reports
 *   no control=reconnecting; otherwise registration is retried for up to 10 minutes
 */
import type { NodeState, SipState } from '@ccphone/contracts';
import type { z } from 'zod';
import type { Logger } from '../log.js';
import { type TimerHost, realTimers } from '../keepalive/timers.js';
import { backoffBaseMs, backoffDelayMs, jitterMs } from '../relay/backoff.js';

export type SipStateName = z.infer<typeof SipState>;

export interface RelayHealth {
  healthy: boolean;
  control: 'ok' | 'reconnecting' | 'down';
}

export const REGISTER_RETRY_WINDOW_MS = 10 * 60 * 1000;
export const REGISTER_RETRY_INTERVAL_MS = 30_000;

/** pure: is a 401/403 on REGISTER a revocation right now? */
export function registerRejectIsRevocation(status: number, relay: RelayHealth): boolean {
  if (status !== 401 && status !== 403) return false;
  return relay.healthy && relay.control !== 'reconnecting';
}

export interface SipReconnectorDeps {
  reconnect(): Promise<void>;
  register(): Promise<void>;
  relayHealth(): RelayHealth;
  onState(s: SipStateName): void;
  /** the session credential is dead: stop everything and show the re-launch card */
  onRevoked(reason: 'register_rejected' | 'register_retry_exhausted'): void;
  log: Logger;
  timers?: TimerHost;
  random?: () => number;
  now?: () => number;
}

export class SipReconnector {
  private attempt = 0;
  private timer: unknown = null;
  private held = false;
  private nodeState: NodeState = 'up';
  private stopped = false;
  private connecting = false;
  private rejectSince: number | null = null;
  private registerRetryTimer: unknown = null;
  private readonly timers: TimerHost;
  private readonly random: () => number;
  private readonly now: () => number;

  constructor(private readonly d: SipReconnectorDeps) {
    this.timers = d.timers ?? realTimers;
    this.random = d.random ?? Math.random;
    this.now = d.now ?? Date.now;
  }

  get isHeld(): boolean {
    return this.held;
  }

  get attempts(): number {
    return this.attempt;
  }

  get scheduled(): boolean {
    return this.timer !== null;
  }

  stop(): void {
    this.stopped = true;
    this.clear();
  }

  private clear(): void {
    if (this.timer !== null) this.timers.clearTimeout(this.timer);
    this.timer = null;
    if (this.registerRetryTimer !== null) this.timers.clearTimeout(this.registerRetryTimer);
    this.registerRetryTimer = null;
  }

  /** the transport dropped (or connect failed) */
  onDisconnected(): void {
    if (this.stopped) return;
    this.d.onState('reconnecting');
    this.schedule();
  }

  /** the transport is up and REGISTER succeeded */
  onRegistered(): void {
    this.attempt = 0;
    this.rejectSince = null;
    if (this.registerRetryTimer !== null) this.timers.clearTimeout(this.registerRetryTimer);
    this.registerRetryTimer = null;
    this.d.onState('registered');
  }

  setNodeState(s: NodeState): void {
    const wasHeld = this.held;
    this.nodeState = s;
    if (s === 'restarting' || s === 'down') {
      if (this.timer !== null) {
        this.timers.clearTimeout(this.timer);
        this.timer = null;
      }
      if (!this.held) this.d.log.info({ node_state: s }, 'SIP reconnects held');
      this.held = true;
      return;
    }
    if (wasHeld && s === 'up') {
      this.held = false;
      this.attempt = 0;
      const delay = jitterMs(backoffBaseMs(0), this.random);
      this.d.log.info({ delay_ms: delay }, 'node up: SIP reconnect released');
      this.armIn(delay);
    } else if (wasHeld) {
      // draining: keep holding until up
      this.held = true;
    }
  }

  /** sleep/resume, IP change, Retry now: skip the ladder */
  kick(): void {
    if (this.stopped || this.connecting) return;
    if (this.held && this.nodeState !== 'up') return;
    this.clear();
    void this.run();
  }

  private schedule(): void {
    if (this.stopped || this.timer !== null || this.connecting) return;
    if (this.held) {
      this.d.log.info({ node_state: this.nodeState }, 'SIP reconnect held by node_state');
      return;
    }
    const delay = backoffDelayMs(this.attempt, this.random);
    this.attempt += 1;
    this.d.log.info({ attempt: this.attempt, delay_ms: delay }, 'SIP reconnect scheduled');
    this.armIn(delay);
  }

  private armIn(delay: number): void {
    if (this.timer !== null) this.timers.clearTimeout(this.timer);
    this.timer = this.timers.setTimeout(() => {
      this.timer = null;
      void this.run();
    }, delay);
  }

  private async run(): Promise<void> {
    if (this.stopped || this.connecting) return;
    this.connecting = true;
    this.d.onState('connecting');
    try {
      await this.d.reconnect();
      await this.d.register();
      // onRegistered() arrives through the registerer state change
    } catch (e) {
      this.d.log.warn({ err: (e as Error).message, attempt: this.attempt }, 'SIP reconnect failed');
      this.connecting = false;
      this.d.onState('reconnecting');
      this.schedule();
      return;
    }
    this.connecting = false;
  }

  /** a REGISTER (refresh) came back with `status` */
  onRegisterRejected(status: number): 'revoked' | 'retry' | 'other' {
    if (this.stopped) return 'other';
    if (status !== 401 && status !== 403) {
      this.d.onState('unregistered');
      this.schedule();
      return 'other';
    }
    const health = this.d.relayHealth();
    if (registerRejectIsRevocation(status, health)) {
      this.d.log.warn({ status }, 'REGISTER rejected with the relay healthy: revocation');
      this.d.onState('failed');
      this.stop();
      this.d.onRevoked('register_rejected');
      return 'revoked';
    }
    const t = this.now();
    if (this.rejectSince === null) this.rejectSince = t;
    if (t - this.rejectSince > REGISTER_RETRY_WINDOW_MS) {
      this.d.log.warn({ status, since_ms: t - this.rejectSince }, 'REGISTER still rejected after 10 min');
      this.d.onState('failed');
      this.stop();
      this.d.onRevoked('register_retry_exhausted');
      return 'revoked';
    }
    this.d.log.info({ status, relay_healthy: health.healthy, control: health.control }, 'REGISTER rejected while control is degraded: retrying');
    this.d.onState('reconnecting');
    if (this.registerRetryTimer === null) {
      this.registerRetryTimer = this.timers.setTimeout(() => {
        this.registerRetryTimer = null;
        this.d.register().catch((e: Error) => this.d.log.warn({ err: e.message }, 'REGISTER retry failed'));
      }, REGISTER_RETRY_INTERVAL_MS);
    }
    return 'retry';
  }
}
